import React, { memo, useState, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import { getDateRange } from '../../../../../../../../shared/components/date-filter/date-filter-options';
import TeamFilter from '../../../../../../../reports/components/reports-filters/components/teams-filter';
import { accessibleOnClick } from '../../../../../../../../shared/utils';
import { useOutClickHook } from '../../../../../../../../shared/utils/hooks';
import DateFilter from '../../../../../../../../shared/components/date-filter';
import * as Icons from '../../../../../../../../shared/svg';
import './reports-sub-header.scss';
import hasPermission from '../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../shared/utils/access-control/enums/permissions';
import GlobalRestrictionModal from '../../../../../../../home/components/global-restriction-modal';
import { useCanShowDataExport } from '../../../../../../../../shared/utils/data-export-permission';
import { useExportConfirmation } from '../../../../../../../../shared/components/export-confirmation/use-export-confirmation';

export type CallDirectionFilter = 'outgoing' | 'incoming';

export interface ReportFilters {
  userIds: number[];
  startDate: string;
  endDate: string;
}

interface DateFilterState {
  label: string;
  startDate: DateTime;
  endDate: DateTime;
  displayText: string;
}

interface IProps {
  timeZone: string;
  onExport?: () => void | Promise<void>;
  onDetailedExport?: () => void | Promise<void>;
  callDirection: CallDirectionFilter;
  onCallDirectionChange: (direction: CallDirectionFilter) => void;
  onFiltersChange?: (filters: ReportFilters) => void;
  selectedUserIds?: number[];
  isDemoAccount: boolean;
}

const DIRECTION_OPTIONS = [
  { key: 'outgoing' as const, label: 'Outgoing' },
  { key: 'incoming' as const, label: 'Incoming' },
];

const ReportsSubHeader: React.FC<IProps> = memo(
  ({
    timeZone,
    onExport,
    onDetailedExport,
    callDirection,
    onCallDirectionChange,
    onFiltersChange,
    selectedUserIds = [],
    isDemoAccount,
  }) => {
    const canDisplayDataExport = useCanShowDataExport();
    const { guardExport, exportConfirmationModal } = useExportConfirmation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showRestrictedModal, setShowRestrictedModal] = useState(false);
    const dropdownRef = useRef(null);

    const toggleDropdown = () => setShowDropdown((prev) => !prev);
    const closeDropdown = () => setShowDropdown(false);
    useOutClickHook(dropdownRef, closeDropdown);

    const { startDate, endDate } = getDateRange('last_30_days', timeZone);
    const [date, setDate] = useState<DateFilterState>({
      label: 'last_30_days',
      startDate,
      endDate,
      displayText: 'Last 30 Days',
    });

    const onDateTimeSelectionChange = useCallback(
      (label: string, start: DateTime, end: DateTime, displayText: string) => {
        setDate({ label, startDate: start, endDate: end, displayText });
        onFiltersChange?.({
          userIds: selectedUserIds,
          startDate: start.toISO(),
          endDate: end.toISO(),
        });
      },
      [selectedUserIds, onFiltersChange],
    );

    const handleTableBreakdownClick = useCallback(() => {
      if (isDemoAccount) {
        setShowRestrictedModal(true);
        return;
      }
      closeDropdown();
      onExport?.();
    }, [onExport, isDemoAccount]);

    const handleDetailedReportClick = useCallback(() => {
      if (isDemoAccount) {
        setShowRestrictedModal(true);
        return;
      }
      closeDropdown();
      onDetailedExport?.();
    }, [onDetailedExport, isDemoAccount]);

    return (
      <div className="call-logs-reports-sub-header">
        <div className="call-logs-reports-sub-header__left">
          <div className="call-logs-reports-sub-header__toggle global-tab-style report-tab d-flex w-auto">
            {DIRECTION_OPTIONS?.map((option) => (
              <div
                className="call-logs-reports-sub-header__toggle-item"
                {...accessibleOnClick(() => onCallDirectionChange(option.key))}
              >
                <span
                  className={`d-flex justify-content-center align-items-center semibold-1 ${
                    callDirection === option.key
                      ? 'active blue-txt-11'
                      : 'gray-txt-11'
                  }`}
                >
                  {option.label}
                </span>
              </div>
            ))}
          </div>

          <div className="call-logs-reports-sub-header__filters">
            {(hasPermission(Permissions.TEAM_PHONE_CALL_READ) ||
              hasPermission(Permissions.ACCOUNT_PHONE_CALL_READ)) && (
              <TeamFilter
                usersIds={selectedUserIds}
                onUserIdsChange={(ids) => {
                  onFiltersChange?.({
                    userIds: ids,
                    startDate: date.startDate.toISO(),
                    endDate: date.endDate.toISO(),
                  });
                }}
              />
            )}
            <DateFilter
              selectedDateRangeOption={date.label}
              startDate={date.startDate}
              endDate={date.endDate}
              displayText={date.displayText}
              onDateTimeSelectionChange={onDateTimeSelectionChange}
              shouldHaveMinMaxValueLimit={false}
              shouldShowDateTooltip={true}
              shouldHaveMaxValueLimit={true}
            />
          </div>
        </div>

        {canDisplayDataExport && (
          <div className="call-logs-reports-sub-header__right" ref={dropdownRef}>
            <div
              className="d-flex align-items-center pointer"
              {...accessibleOnClick(toggleDropdown)}
            >
              <Icons.Download
                height={20}
                width={20}
                className={`mr-2 font-20 ${
                  showDropdown ? 'blue-txt-11' : 'gray-txt-15'
                }`}
              />
              <span
                className={`export-dropdown-span ${
                  showDropdown ? 'blue-txt-11' : 'gray-txt-15'
                }`}
              >
                Export
              </span>
            </div>
            {showDropdown && (
              <div className="export-dropdown-view">
                <div className="list-unstyled">
                  <div
                    className="menu-item"
                    {...accessibleOnClick(() =>
                      guardExport(handleDetailedReportClick),
                    )}
                  >
                    <span className="regular-2 font-medium menu-text">
                      Detailed Report
                    </span>
                  </div>
                  <div
                    className="menu-item"
                    {...accessibleOnClick(() =>
                      guardExport(handleTableBreakdownClick),
                    )}
                  >
                    <span className="regular-2 font-medium menu-text">
                      Table Breakdown
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <GlobalRestrictionModal
          show={showRestrictedModal}
          onClose={() => setShowRestrictedModal(false)}
          title="Export is disabled in demo mode"
          content="This is a demo account with sample data. Signup to export your real leads, contacts, or reports"
          primaryBtnLabel="Sign up"
        />
        {exportConfirmationModal}
      </div>
    );
  },
);

ReportsSubHeader.displayName = 'ReportsSubHeader';

export default ReportsSubHeader;
